import { Fragment, useEffect, useRef, useState } from 'react'
import { NotificationsMenu } from './NotificationsMenu'
import { DASHBOARD_TOPBAR_ACTIONS_ID, DASHBOARD_TOPBAR_END_ID } from './DashboardTopbarActions'
import { WIDGET_TOOLBAR_SLOT_ID } from './ProjectWorkspace/CustomizableDashboardGrid'
import { DashboardSearch } from './DashboardSearch'
import type { SearchTarget } from '../lib/dashboardSearch'
import { IconChevronDown, IconMenu2, IconX } from '@tabler/icons-react'
import { BrandLockup } from './BrandLockup'
import { BrandBackPill } from './BrandBackPill'
import { useI18n } from '../lib/i18n'
import {
  ProjectPickerMenu,
  type SidebarDeletedProject,
  type SidebarProject,
} from './ProjectWorkspace/SidebarProjectCard'

type ProjectBreadcrumb = {
  currentProjectId: string
  currentProjectName: string
  /** True while the project is still loading and the name isn't known yet. */
  loading?: boolean
  projects: SidebarProject[]
  deletedProjects?: SidebarDeletedProject[]
  /** El camino hasta donde estás: «Dominio», o «Configuración / Dominio». */
  sectionTrail: string[]
  onSelectProject: (projectId: string) => void
  onOpenDeleted?: (project: SidebarDeletedProject) => void
  onCreateProject: () => void
}

export function DashboardTopbar({
  mobileNavOpen,
  onToggleMobileNav,
  token,
  onHome,
  projectBreadcrumb,
  projectId,
  backToDashboard = false,
  onSearchGo,
}: {
  mobileNavOpen: boolean
  onToggleMobileNav: () => void
  token: string
  onHome?: () => void
  /**
   * El proyecto, el área y el camino. Con esto la barra se pone su forma de
   * dentro: la pastilla con la marca, «Dashboard ⌄» y el migajero.
   *
   * Sin esto la barra se queda desnuda —marca entera, sin caja— y es lo que
   * quiere la pantalla de empezar un proyecto: ahí no se está EN ninguna
   * sección, así que decir «Dashboard / Empresa / Inicio» sería nombrar un
   * sitio en el que no se está.
   */
  projectBreadcrumb?: ProjectBreadcrumb
  /**
   * Sólo para el buscador, cuando hay proyecto pero no migajero.
   *
   * Es el caso de la pantalla de empezar: se llega desde un proyecto abierto y
   * se puede seguir buscando dentro de él, pero la barra no lo anuncia porque
   * no se está en él. Con migajero, el proyecto sale de ahí.
   */
  projectId?: string | undefined
  /**
   * La barra como SALIDA: la pastilla con la mascota y «Dashboard».
   *
   * Es lo que quiere la pantalla de empezar un proyecto. Ahí no se está en
   * ninguna sección —así que no hay migajero que poner— pero sí se llega desde
   * el panel, y hace falta la puerta de vuelta. El logotipo entero no la da:
   * dice de quién es la casa, no cómo se sale de la habitación.
   *
   * Es la misma pastilla de dentro de un proyecto, sin el desplegable de área:
   * aquí no hay editor ni CMS a los que ir, sólo el camino de vuelta.
   */
  backToDashboard?: boolean
  /**
   * A dónde llevar lo que se busque. Sin esto no hay buscador.
   *
   * Es opcional porque fuera de un proyecto la mitad de los destinos no existen:
   * no hay páginas ni analíticas que abrir, y un buscador que devuelve sitios a
   * los que no puede ir es peor que no tenerlo.
   */
  onSearchGo?: ((target: SearchTarget) => void) | undefined
}) {
  const { t } = useI18n()
  const [projectMenuOpen, setProjectMenuOpen] = useState(false)
  const projectMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!projectMenuOpen) return
    const onPointerDown = (event: PointerEvent) => {
      if (!projectMenuRef.current?.contains(event.target as Node)) setProjectMenuOpen(false)
    }
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setProjectMenuOpen(false) }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [projectMenuOpen])

  return (
    <header className="dash-topbar">
      <button
        className="dash-icon-btn dash-mobile-menu-btn"
        type="button"
        aria-label={mobileNavOpen ? t('topbar.closeMenu') : t('topbar.openMenu')}
        aria-expanded={mobileNavOpen}
        onClick={onToggleMobileNav}
      >
        {mobileNavOpen ? <IconX size={15} /> : <IconMenu2 size={15} />}
      </button>

      <div className="dash-topbar-context">
        {/* La misma pastilla que la barra del editor: mascota, Beta y área en
            una sola pieza; el migajero de empresa y sección sigue detrás, sin
            cambios. */}
        {/* Dos cajas: la de fuera (`dash-topbar-brand`) conserva el espacio
            hasta el card grande para que el migajero arranque a su ras, y la de
            dentro (`dash-brand-cluster`) tiene el ancho exacto del botón Home
            del menú lateral (222px) para alinearse en la misma columna visual. */}
        <div className="dash-topbar-brand">
          {/* Dentro de un proyecto, la marca va en su pastilla, delante del
              migajero; fuera de él no hay nada que agrupar y va sin caja, como
              en cualquier portada. En los dos casos, la marca entera. */}
          {backToDashboard ? <BrandBackPill onClick={() => onHome?.()} /> : (
          <div className={`dash-brand-cluster${projectBreadcrumb ? '' : ' is-bare'}`}>
          {/* Mascota, logotipo y «beta», con la MISMA firma que los asistentes
              (`dash-onboarding-brand-*`): son la misma marca y no pueden pesar
              distinto según por dónde se entre. Dentro de un proyecto también:
              el selector de área («Dashboard ▾») que ocupaba ese sitio se quitó.

              Con medidas en los atributos: sin ellas el hueco vale cero hasta
              que llega el dibujo y la barra da un salto al aparecer. Las del
              logotipo salen de su proporción real, 167 × 46. */}
          <button
            className={`dash-topbar-home${projectBreadcrumb ? '' : ' dash-onboarding-brand-lockup'}`}
            type="button"
            aria-label={t('topbar.home')}
            onClick={onHome}
            /* Sin destino, es sólo la marca: ni se pulsa ni se le llega con el tabulador. */
            {...(onHome ? {} : { tabIndex: -1, 'aria-disabled': true, style: { cursor: 'default' } })}
          >
            <BrandLockup
              mascotClassName={projectBreadcrumb ? 'dash-topbar-logo dash-topbar-logo--mark' : 'dash-onboarding-brand-mascot'}
              mascotSize={projectBreadcrumb ? 23 : 28}
            />
          </button>
          </div>
          )}
        </div>

        {/* El nombre también abre el selector: en un migajero el proyecto se
            lee naturalmente como el contexto al que se puede volver o cambiar.
            El selector del menú lateral se conserva para que la misma acción
            siga disponible cuando se navega por esa columna. */}
        {projectBreadcrumb ? (
          <nav className="dash-project-breadcrumb" aria-label={t('topbar.breadcrumbAria')}>
            <span className="dash-project-breadcrumb-separator" aria-hidden="true">/</span>
            <div className="dash-project-switcher" ref={projectMenuRef}>
              <button
                className="dash-project-switcher-trigger"
                type="button"
                aria-haspopup="menu"
                aria-expanded={projectMenuOpen}
                aria-label={t('sidebar.project.aria', {
                  name: projectBreadcrumb.currentProjectName || t('topbar.projectFallback'),
                })}
                onClick={() => {
                  setProjectMenuOpen((open) => !open)
                }}
              >
                {projectBreadcrumb.loading && !projectBreadcrumb.currentProjectName ? (
                  <span className="sk dash-breadcrumb-skeleton" aria-hidden="true" />
                ) : (
                  <span>{projectBreadcrumb.currentProjectName || t('topbar.projectFallback')}</span>
                )}
                <IconChevronDown size={14} aria-hidden="true" />
              </button>

              {projectMenuOpen ? (
                <ProjectPickerMenu
                  className="dash-project-picker-menu--topbar"
                  projects={projectBreadcrumb.projects}
                  currentProjectId={projectBreadcrumb.currentProjectId}
                  deletedProjects={projectBreadcrumb.deletedProjects ?? []}
                  onSelectProject={projectBreadcrumb.onSelectProject}
                  {...(projectBreadcrumb.onOpenDeleted ? { onOpenDeleted: projectBreadcrumb.onOpenDeleted } : {})}
                  onCreateProject={projectBreadcrumb.onCreateProject}
                  onClose={() => setProjectMenuOpen(false)}
                />
              ) : null}
            </div>
            {projectBreadcrumb.sectionTrail.map((step, index) => (
              <Fragment key={step}>
                <span
                  className="dash-project-breadcrumb-separator dash-project-breadcrumb-section-separator"
                  aria-hidden="true"
                >/</span>
                {/* El último escalón es donde estás; los de antes, el camino.
                    En el mismo gris se leerían como tres sitios a la vez. */}
                <span
                  className={index === projectBreadcrumb.sectionTrail.length - 1
                    ? 'dash-project-breadcrumb-section'
                    : 'dash-project-breadcrumb-parent'}
                >{step}</span>
              </Fragment>
            ))}
          </nav>
        ) : null}

        {/* Guardar y descartar, en el hueco que queda tras el migajero —o tras la
            marca en estrecho— y antes del buscador, centrados en él. Al final de
            la barra quedaban pegados al borde derecho de la página, lejos de lo
            que se estaba editando. Vive DENTRO del contexto y no suelto en la
            barra para no cambiar cómo se reparte el sitio: el contexto sigue
            siendo quien absorbe el apretón, y el migajero cede como siempre. El
            hueco crece y los centra; vacío no se ve. Lo rellena
            `PendingChangesActions` por su id. */}
        <div className="dash-topbar-pending-slot" id={DASHBOARD_TOPBAR_ACTIONS_ID}>
          {/* Editando widgets, sus mandos —«Agregar» y «Listo»— en el mismo
              hueco centrado y con la misma cápsula oscura que guardar y
              descartar: los dos son «estás cambiando algo». Vacío el resto del
              tiempo, y entonces no se ve. Ver `WIDGET_TOOLBAR_SLOT_ID`. */}
          <div className="dash-topbar-widget-slot" id={WIDGET_TOOLBAR_SLOT_ID} />
        </div>
      </div>

      {/* En medio, entre el proyecto y la campana: el buscador no es del
          proyecto ni es una acción, y en cualquiera de los dos extremos se leería
          como parte de su grupo. */}
      {onSearchGo ? (
        <DashboardSearch
          onGo={onSearchGo}
          token={token}
          {...(projectBreadcrumb?.currentProjectId ?? projectId
            ? { projectId: (projectBreadcrumb?.currentProjectId ?? projectId) as string }
            : {})}
        />
      ) : null}

      <div className="dash-top-actions">
        {/* La campana es del proyecto abierto, no de la cuenta: ver la nota en
            `NotificationsMenu`. Sin proyecto —el panel de inicio— enseña sólo lo
            que es de la cuenta. */}
        <NotificationsMenu
          token={token}
          {...(projectBreadcrumb?.currentProjectId ?? projectId
            ? { projectId: (projectBreadcrumb?.currentProjectId ?? projectId) as string }
            : {})}
        />
        {/* El final de la barra: lo que una pantalla sin menú lateral necesita
            tener a mano, como el menú de usuario. */}
        <div className="dash-topbar-end-slot" id={DASHBOARD_TOPBAR_END_ID} />
      </div>

    </header>
  )
}
